import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { DashboardProvider } from "@/context/dashboard";
import Dashboard from "@/Layouts/Dashboard";
import { formatMonthDayYear } from "@/Utils/dateFormat";
import { decryptInteger } from "@/Utils/encryptInteger";

const OrderCard = ({ show, orders, booking_id }) => {
    const router = useRouter();

    return (
        <div className="order_card">
            <div className="order_card_thumbnail">
                <div className="order_card_thumbnail_show">
                    <img src={show.show_banner} alt={show.name} />
                    <div>
                        <p>{show.name}</p>
                    </div>
                </div>
                <div>
                    <button
                        className="view_button main_btn_bg"
                        onClick={() => router.push(`/tickets/${booking_id}`)}
                    >
                        View Ticket
                    </button>
                </div>
            </div>

            <div className="order_card_orders">
                <div className="order_card_header">
                    <p className="order_header_cell order_no">Order ID</p>
                    <p className="order_header_cell order_date">Purchased date</p>
                    <p className="order_header_cell order_quantity">Quantity</p>
                    <p className="order_header_cell order_total">Total</p>
                </div>
                {orders.map((order, index) => (
                    <div className="order_card_orders_item" key={index}>
                        <p className="order_cell order_no">{order.order_id}</p>
                        <p className="order_cell order_date">{formatMonthDayYear(order.purchase_date.split(" ")[0])}</p>
                        <p className="order_cell order_quantity">{order.total_ticket_in_this_order} Tickets</p>
                        <p className="order_cell order_total">${order.total}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function Orders() {
    const [ordersData, setOrdersData] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const scrollRef = useRef();
    function getToken() {
        try {
            let encryptedData = localStorage.getItem("user_data");
            if (encryptedData) {
                let data = JSON.parse(decryptInteger(encryptedData));
                return data?.web_token || null;
            }
            return null;
        } catch (error) {
            console.error("Error fetching web token:", error);
            return null;
        }
    }

    const fetchOrders = async (initial = false) => {
        if (!hasMore && !initial) return;
        setLoading(true);

        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageOrder`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({
                    action: "getOrderHistory",
                    page_index: String(initial ? 0 : pageIndex),
                }),
            });

            const data = await res.json();
            const newOrders = data?.data || [];

            if (newOrders.length === 0) {
                setHasMore(false);
            } else {
                if (initial) {
                    setOrdersData(newOrders);
                } else {
                    setOrdersData(prev => [...prev, ...newOrders]);
                }
                setPageIndex(prev => prev + 1);
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(true); // initial fetch
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const isMobile = window.innerWidth < 768;
            if (loading || !hasMore) return;

            if (isMobile) {
                const scrollTop = window.scrollY;
                const windowHeight = window.innerHeight;
                const scrollHeight = document.documentElement.scrollHeight;

                if (scrollTop + windowHeight >= scrollHeight - 100) {
                    fetchOrders();
                }
            } else {
                const el = scrollRef.current;
                if (!el) return;

                const { scrollTop, scrollHeight, clientHeight } = el;
                if (scrollTop + clientHeight >= scrollHeight - 50) {
                    fetchOrders();
                }
            }
        };

        const isMobile = window.innerWidth < 768;
        const target = isMobile ? window : scrollRef.current;

        target?.addEventListener("scroll", handleScroll);
        return () => target?.removeEventListener("scroll", handleScroll);
    }, [loading, hasMore]);

    return (
        <DashboardProvider>
            <Dashboard>
                <div
                    id="profile-container"
                    ref={scrollRef}
                    className="custom_scrll_bar lg:max-h-[calc(100vh-90px)] pt-5 lg:pt-5 px-6 lg:px-10 pb-4 overflow-auto"
                >
                    <h2 className="dashboard-heading pl-3 lg:pl-0 mt-0">Orders</h2>
                    <div className="mt-7 rounded-[16px] w-full">
                        {ordersData.length > 0 ? (
                            ordersData.map((group) => (
                                <div className="py-2" key={group.booking_id}>
                                    <OrderCard show={group.show_data} orders={group.orders} booking_id={group.booking_id} />
                                </div>
                            ))
                        ) : (
                            !loading && (
                                <p className="text-center text-gray-400 py-8">
                                    No orders found.
                                </p>
                            )
                        )}
                        {loading && <p className="text-center py-4">Loading...</p>}
                        {!hasMore && ordersData.length > 0 && (
                            <p className="text-center py-4 text-gray-400">No more orders</p>
                        )}
                    </div>
                </div>
            </Dashboard>
        </DashboardProvider>
    );
}
